// react
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// material ui icons
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

// material ui
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Collapse,
  IconButton,
  Snackbar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// components
import { hideSnackbar } from '@/redux/snackbarSlice';

const getSeverityIcon = (severity) => {
  switch (severity) {
    case 'success':
      return <CheckCircleRoundedIcon fontSize="inherit" />;
    case 'error':
      return <ErrorRoundedIcon fontSize="inherit" />;
    case 'warning':
      return <WarningRoundedIcon fontSize="inherit" />;
    default:
      return null;
  }
};

const AUTO_HIDE_MS = 6000;

const SnackbarComponent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const snackbars = useSelector((state) => state.snackbar.queue);

  // Track which snackbars have their "response" expanded
  const [expandedIds, setExpandedIds] = useState({});

  // Track which snackbars should currently be "open" (exist in queue)
  // We keep this as derived state so we can close via Snackbar's events.
  const openMap = useMemo(() => {
    const m = {};
    snackbars.forEach((s) => (m[s.id] = true));
    return m;
  }, [snackbars]);

  // Timers for auto-hide behavior (paused while expanded)
  const timerRefs = useRef({});

  useEffect(() => {
    snackbars.forEach((snackbar) => {
      const { id } = snackbar;

      // If expanded, do not auto-hide
      if (expandedIds[id]) return;

      // If timer already exists, don't recreate
      if (!timerRefs.current[id]) {
        timerRefs.current[id] = setTimeout(() => {
          dispatch(hideSnackbar(id));
          delete timerRefs.current[id];
        }, AUTO_HIDE_MS);
      }
    });

    // Cleanup timers for snackbars no longer present
    const currentIds = new Set(snackbars.map((s) => s.id));
    Object.keys(timerRefs.current).forEach((id) => {
      if (!currentIds.has(id)) {
        clearTimeout(timerRefs.current[id]);
        delete timerRefs.current[id];
      }
    });

    return () => {
      // On unmount
      Object.values(timerRefs.current).forEach(clearTimeout);
      timerRefs.current = {};
    };
  }, [snackbars, expandedIds, dispatch]);

  const handleClose = (id) => (_, reason) => {
    // Avoid closing on clickaway if you prefer the user must dismiss explicitly
    if (reason === 'clickaway') return;
    dispatch(hideSnackbar(id));
  };

  const toggleExpand = (id) => {
    setExpandedIds((prev) => {
      const nextExpanded = !prev[id];

      // Pause auto-hide when expanding
      if (nextExpanded && timerRefs.current[id]) {
        clearTimeout(timerRefs.current[id]);
        delete timerRefs.current[id];
      }

      return { ...prev, [id]: nextExpanded };
    });
  };

  return (
    <>
      {snackbars.map((snackbar, index) => {
        const hasResponse = Boolean(snackbar.response);
        const expanded = Boolean(expandedIds[snackbar.id]);

        return (
          <Snackbar
            key={snackbar.id}
            open={Boolean(openMap[snackbar.id])}
            onClose={handleClose(snackbar.id)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            autoHideDuration={5000}
            // Stack upward: first item closest to bottom, next above it, etc.
            sx={{
              mb: `${16 + index * 70}px`,
              borderRadius: 2,
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.background.paper
                  : theme.palette.grey[800],
            }}
          >
            {/* Use a fixed-width wrapper so stacking calculations are stable */}

            <Badge
              badgeContent={snackbar.count === 1 ? 0 : snackbar.count}
              color="primary"
            >
              <Alert
                icon={getSeverityIcon(snackbar.severity)}
                severity={snackbar.severity}
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  // borderLeft: `6px solid ${
                  //   theme.palette[snackbar.severity]?.main ||
                  //   theme.palette.primary.main
                  // }`,
                  color: theme.palette.text.primary,
                  alignItems: 'flex-start',
                  width: '100%',
                }}
                action={
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => dispatch(hideSnackbar(snackbar.id))}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <AlertTitle sx={{ m: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      width: '100%',
                    }}
                  >
                    <Box sx={{ flex: 1, pr: 1 }}>{snackbar.message}</Box>

                    {hasResponse && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(snackbar.id)}
                        aria-label={expanded ? 'collapse' : 'expand'}
                      >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </Box>
                </AlertTitle>

                {hasResponse && (
                  <Collapse in={expanded}>
                    <Box
                      component="pre"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mt: 1,
                        mb: 0,
                        fontSize: '0.85rem',
                        // bgcolor:
                        //   theme.palette.mode === 'light'
                        //     ? 'grey.100'
                        //     : 'grey.900',
                        p: 1,
                        borderRadius: 1,
                      }}
                    >
                      {typeof snackbar.response === 'string'
                        ? snackbar.response
                        : JSON.stringify(snackbar.response, null, 2)}
                    </Box>
                  </Collapse>
                )}
              </Alert>
            </Badge>
          </Snackbar>
        );
      })}
    </>
  );
};

export default SnackbarComponent;
